import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * useKamSession Hook
 * Tracks user activity and maintains Kam's session memory
 *
 * Usage:
 * const {
 *   sessionId,
 *   logActivity,
 *   updateContext,
 *   saveInsight
 * } = useKamSession(userId, brandId);
 */
export const useKamSession = (userId, brandId = null) => {
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sessionRef = useRef(null);

  // Initialize or get active session
  useEffect(() => {
    const initSession = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get('/api/kam-session/active', {
          params: { userId, brandId }
        });

        if (response.data.success) {
          setSessionId(response.data.sessionId);
          sessionRef.current = response.data.session;
        }
      } catch (err) {
        console.error('Failed to initialize Kam session:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initSession();
  }, [userId, brandId]);

  /**
   * Log user activity to timeline
   * @param {string} page - Current page (collection-plan, design-studio, etc.)
   * @param {string} action - What the user did
   * @param {object} details - Additional context
   * @param {string} kamNotes - Kam's interpretation (optional)
   */
  const logActivity = useCallback(async (page, action, details = {}, kamNotes = '') => {
    if (!sessionId) return;

    try {
      await axios.post('/api/kam-session/activity', {
        sessionId,
        page,
        action,
        details,
        kamNotes
      });
    } catch (err) {
      console.error('Failed to log activity:', err);
    }
  }, [sessionId]);

  /**
   * Update current context (what user is working on)
   * @param {object} context - Current work context
   */
  const updateContext = useCallback(async (context) => {
    if (!sessionId) return;

    try {
      await axios.post('/api/kam-session/context', {
        sessionId,
        context
      });
    } catch (err) {
      console.error('Failed to update context:', err);
    }
  }, [sessionId]);

  /**
   * Save mood board analysis
   * @param {array} images - Image URLs
   * @param {string} analysis - Kam's analysis
   * @param {array} themes - Extracted themes
   * @param {array} colors - Color palette
   */
  const saveMoodBoardAnalysis = useCallback(async (images, analysis, themes = [], colors = []) => {
    if (!sessionId) return;

    try {
      const response = await axios.post('/api/kam-session/insight/moodboard', {
        sessionId,
        images,
        analysis,
        themes,
        colors
      });
      return response.data.analysisId;
    } catch (err) {
      console.error('Failed to save mood board analysis:', err);
      throw err;
    }
  }, [sessionId]);

  /**
   * Save sketch analysis
   * @param {string} sketchUrl - Sketch image URL
   * @param {string} analysis - Kam's full analysis
   * @param {object} suggestions - Suggested garment details
   */
  const saveSketchAnalysis = useCallback(async (sketchUrl, analysis, suggestions = {}) => {
    if (!sessionId) return;

    try {
      const response = await axios.post('/api/kam-session/insight/sketch', {
        sessionId,
        sketchUrl,
        analysis,
        suggestions
      });
      return response.data.analysisId;
    } catch (err) {
      console.error('Failed to save sketch analysis:', err);
      throw err;
    }
  }, [sessionId]);

  /**
   * Save generated YAML
   * @param {string} buildId - Garment build ID
   * @param {string} garmentName - Garment name
   * @param {string} yaml - Full YAML specification
   * @param {string} sourceSketch - Source sketch reference
   * @param {string} notes - Additional notes
   */
  const saveYAML = useCallback(async (buildId, garmentName, yaml, sourceSketch = '', notes = '') => {
    if (!sessionId) return;

    try {
      const response = await axios.post('/api/kam-session/insight/yaml', {
        sessionId,
        buildId,
        garmentName,
        yaml,
        sourceSketch,
        notes
      });
      return response.data.yamlId;
    } catch (err) {
      console.error('Failed to save YAML:', err);
      throw err;
    }
  }, [sessionId]);

  /**
   * End the current session
   */
  const endSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await axios.post(`/api/kam-session/${sessionId}/end`);
      return response.data.summary;
    } catch (err) {
      console.error('Failed to end session:', err);
      throw err;
    }
  }, [sessionId]);

  return {
    sessionId,
    loading,
    error,
    session: sessionRef.current,

    // Methods
    logActivity,
    updateContext,
    saveMoodBoardAnalysis,
    saveSketchAnalysis,
    saveYAML,
    endSession
  };
};

export default useKamSession;
